import { useSettingsStore } from '@/stores';
import { Smartphone, UserCheck, Clock, PenTool, Calendar } from 'lucide-react';
import { clsx } from 'clsx';
import type { WhatsappMode } from '@/types/settings';

export function MessagingConfig() {
  const {
    whatsappMode,
    greetingMode,
    greetingPlainPrefix,
    customerNameMode,
    customerAlias,
    nameMode,
    messageTimeMode,
    messageRangeConnector,
    timeMode,
    messageUseFriendlyDate,
    messageIncludeDayName,
    messageUseTomorrowTodayLabels,
    showServices,
    closingText,
    setSetting,
  } = useSettingsStore();

  return (
    <div className="space-y-4">
      {/* 1. Modo de Enlace WhatsApp */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-3 shadow-xs">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
            <Smartphone size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Enlace de Apertura WhatsApp</h3>
            <p className="text-[11px] text-text-muted">Define cómo se abre la aplicación de WhatsApp al pulsar los botones</p>
          </div>
        </div>

        <div className="space-y-1.5 pt-1">
          <select
            value={whatsappMode}
            onChange={(e) => setSetting('whatsappMode', e.target.value as WhatsappMode)}
            className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs text-text-primary cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
          >
            <option value="api">Enlace Universal (api.whatsapp.com) - Recomendado para Móvil y PC</option>
            <option value="wa">Móvil Directo (wa.me)</option>
            <option value="web">WhatsApp Web (web.whatsapp.com) - Forzar Navegador PC</option>
          </select>
        </div>
      </div>

      {/* 2. Tratamiento de Nombres y Saludos */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-4 shadow-xs">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
            <UserCheck size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Tratamiento & Saludos</h3>
            <p className="text-[11px] text-text-muted">Personaliza la cercanía y tono con tus clientes</p>
          </div>
        </div>

        {/* Modo de Saludo */}
        <div className="space-y-2 pt-1 border-t border-border-light">
          <label className="text-xs font-medium text-text-primary block">Modo de Saludo</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSetting('greetingMode', 'dynamic')}
              className={clsx(
                'flex-1 py-1.5 px-3 rounded-lg text-xs font-medium border transition-colors cursor-pointer',
                greetingMode === 'dynamic'
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-surface-secondary text-text-secondary border-border'
              )}
            >
              Dinámico (Buenos días / tardes según la hora)
            </button>
            <button
              type="button"
              onClick={() => setSetting('greetingMode', 'plain')}
              className={clsx(
                'flex-1 py-1.5 px-3 rounded-lg text-xs font-medium border transition-colors cursor-pointer',
                greetingMode === 'plain'
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-surface-secondary text-text-secondary border-border'
              )}
            >
              Plano (Texto Fijo)
            </button>
          </div>

          {greetingMode === 'plain' && (
            <div className="space-y-1.5 pt-1 animate-fade-in">
              <label className="text-[11px] text-text-muted block">Texto de Saludo Fijo</label>
              <input
                type="text"
                value={greetingPlainPrefix}
                onChange={(e) => setSetting('greetingPlainPrefix', e.target.value)}
                placeholder="Ej: Hola"
                className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs"
              />
            </div>
          )}
        </div>

        {/* Modo de Nombre */}
        <div className="space-y-2 pt-2 border-t border-border-light">
          <label className="text-xs font-medium text-text-primary block">Tratamiento de Nombre de Cliente</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSetting('customerNameMode', 'real')}
              className={clsx(
                'flex-1 py-1.5 px-3 rounded-lg text-xs font-medium border transition-colors cursor-pointer',
                customerNameMode === 'real'
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-surface-secondary text-text-secondary border-border'
              )}
            >
              Nombre Real
            </button>
            <button
              type="button"
              onClick={() => setSetting('customerNameMode', 'alias')}
              className={clsx(
                'flex-1 py-1.5 px-3 rounded-lg text-xs font-medium border transition-colors cursor-pointer',
                customerNameMode === 'alias'
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-surface-secondary text-text-secondary border-border'
              )}
            >
              Alias de Confianza
            </button>
          </div>

          {customerNameMode === 'alias' ? (
            <div className="space-y-1.5 pt-1 animate-fade-in">
              <label className="text-[11px] text-text-muted block">Palabra de Alias</label>
              <input
                type="text"
                value={customerAlias}
                onChange={(e) => setSetting('customerAlias', e.target.value)}
                placeholder="Ej: cariño, amiga, reina"
                className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs"
              />
            </div>
          ) : (
            <div className="flex items-center justify-between text-xs pt-1">
              <div>
                <span className="font-medium text-text-primary">Solo Primer Nombre</span>
                <p className="text-[11px] text-text-muted">Si está desactivado se usará el nombre completo</p>
              </div>
              <input
                type="checkbox"
                checked={nameMode === 'first'}
                onChange={(e) => setSetting('nameMode', e.target.checked ? 'first' : 'full')}
                className="w-4 h-4 rounded text-brand-primary focus:ring-brand-primary/30 cursor-pointer accent-brand-primary"
              />
            </div>
          )}
        </div>
      </div>

      {/* 3. Formato de Horas & Firma */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-4 shadow-xs">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
            <Clock size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Formato de Horas & Cierre</h3>
            <p className="text-[11px] text-text-muted">Estructura de tiempo y firma en mensajes</p>
          </div>
        </div>

        {/* Modo de Hora */}
        <div className="space-y-2 pt-1 border-t border-border-light">
          <label className="text-xs font-medium text-text-primary block">Modo de Hora en Citas</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSetting('messageTimeMode', 'start_only')}
              className={clsx(
                'flex-1 py-1.5 px-3 rounded-lg text-xs font-medium border transition-colors cursor-pointer',
                messageTimeMode === 'start_only'
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-surface-secondary text-text-secondary border-border'
              )}
            >
              Solo Inicio (a las 3:00 PM)
            </button>
            <button
              type="button"
              onClick={() => setSetting('messageTimeMode', 'range')}
              className={clsx(
                'flex-1 py-1.5 px-3 rounded-lg text-xs font-medium border transition-colors cursor-pointer',
                messageTimeMode === 'range'
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-surface-secondary text-text-secondary border-border'
              )}
            >
              Rango Completo (a las 3:00 PM hasta 4:30 PM)
            </button>
          </div>

          {messageTimeMode === 'range' && (
            <div className="space-y-1.5 pt-1 animate-fade-in">
              <label className="text-[11px] text-text-muted block">Conector de Rango</label>
              <input
                type="text"
                value={messageRangeConnector}
                onChange={(e) => setSetting('messageRangeConnector', e.target.value)}
                placeholder="Ej: hasta"
                className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs"
              />
            </div>
          )}
        </div>

        {/* 12h vs 24h */}
        <div className="flex items-center justify-between text-xs pt-2 border-t border-border-light">
          <div>
            <span className="font-medium text-text-primary">Formato de 12 Horas (AM/PM)</span>
            <p className="text-[11px] text-text-muted">Si está desactivado se mostrará en formato de 24 horas (ej. 15:00)</p>
          </div>
          <input
            type="checkbox"
            checked={timeMode === '12h'}
            onChange={(e) => setSetting('timeMode', e.target.checked ? '12h' : '24h')}
            className="w-4 h-4 rounded text-brand-primary focus:ring-brand-primary/30 cursor-pointer accent-brand-primary"
          />
        </div>
      </div>

      {/* 3. Formato de Fechas Amigables */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-4 shadow-xs">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
            <Calendar size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Formato de Fechas Amigables</h3>
            <p className="text-[11px] text-text-muted">Personaliza cómo se redactan las fechas en &#123;fecha&#125; y &#123;fecha_hora&#125;</p>
          </div>
        </div>

        {/* Formato Amigable general */}
        <div className="flex items-center justify-between text-xs pt-1 border-t border-border-light">
          <div>
            <span className="font-medium text-text-primary">Formato Amigable en Español</span>
            <p className="text-[11px] text-text-muted">Muestra "19 de agosto" en lugar de "2026-08-19"</p>
          </div>
          <input
            type="checkbox"
            checked={messageUseFriendlyDate}
            onChange={(e) => setSetting('messageUseFriendlyDate', e.target.checked)}
            className="w-4 h-4 rounded text-brand-primary focus:ring-brand-primary/30 cursor-pointer accent-brand-primary"
          />
        </div>

        {/* Usar etiquetas Hoy/Mañana */}
        <div className="flex items-center justify-between text-xs pt-2 border-t border-border-light">
          <div>
            <span className="font-medium text-text-primary">Etiquetas "Hoy", "Mañana" y "Ayer"</span>
            <p className="text-[11px] text-text-muted">Muestra "Hoy 19 de agosto" o "Mañana 20 de agosto" en citas inmediatas</p>
          </div>
          <input
            type="checkbox"
            checked={messageUseTomorrowTodayLabels}
            onChange={(e) => setSetting('messageUseTomorrowTodayLabels', e.target.checked)}
            className="w-4 h-4 rounded text-brand-primary focus:ring-brand-primary/30 cursor-pointer accent-brand-primary"
          />
        </div>

        {/* Incluir día de la semana */}
        <div className="flex items-center justify-between text-xs pt-2 border-t border-border-light">
          <div>
            <span className="font-medium text-text-primary">Incluir Día de la Semana</span>
            <p className="text-[11px] text-text-muted">Añade el nombre del día (ej. "Miércoles 19 de agosto")</p>
          </div>
          <input
            type="checkbox"
            checked={messageIncludeDayName}
            onChange={(e) => setSetting('messageIncludeDayName', e.target.checked)}
            className="w-4 h-4 rounded text-brand-primary focus:ring-brand-primary/30 cursor-pointer accent-brand-primary"
          />
        </div>
      </div>

      {/* 4. Servicios & Firma */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-4 shadow-xs">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
            <PenTool size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Servicios & Firma</h3>
            <p className="text-[11px] text-text-muted">Etiquetas &#123;servicios&#125; y texto de cierre institucional</p>
          </div>
        </div>

        {/* Nombres de Servicios */}
        <div className="flex items-center justify-between text-xs pt-1 border-t border-border-light">
          <div>
            <span className="font-medium text-text-primary">Habilitar nombres de servicios</span>
            <p className="text-[11px] text-text-muted">Permite insertar la etiqueta &#123;de_servicios&#125; y &#123;servicios&#125; en las plantillas</p>
          </div>
          <input
            type="checkbox"
            checked={showServices}
            onChange={(e) => setSetting('showServices', e.target.checked)}
            className="w-4 h-4 rounded text-brand-primary focus:ring-brand-primary/30 cursor-pointer accent-brand-primary"
          />
        </div>

        {/* Firma / Cierre */}
        <div className="space-y-1.5 pt-2 border-t border-border-light">
          <label className="text-xs font-medium text-text-primary flex items-center gap-1.5">
            <PenTool size={13} className="text-brand-primary" />
            <span>Firma / Cierre Institucional (Etiqueta &#123;firma&#125;)</span>
          </label>
          <textarea
            value={closingText}
            onChange={(e) => setSetting('closingText', e.target.value)}
            rows={2}
            className="w-full p-2.5 bg-surface-secondary border border-border rounded-xl text-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
          />
        </div>
      </div>
    </div>
  );
}
